import React, { useEffect, useRef } from 'react';
import { XIcon } from 'lucide-react';
export type LegalDocument = 'privacy' | 'terms';
interface LegalModalProps {
  legalDocument: LegalDocument | null;
  onClose: () => void;
}
const legalContent = {
  privacy: {
    title: 'Privacy Policy',
    sections: [{
      heading: 'Information we collect',
      body: 'When you contact Ginger Briones Studio or request an appointment, we may collect your name, phone number, email address, appointment details, and any information you choose to share with us.'
    }, {
      heading: 'How we use information',
      body: 'We use this information to respond to inquiries, schedule and manage appointments, provide requested services, and communicate important service updates.'
    }, {
      heading: 'Sharing and protection',
      body: 'We do not sell personal information. We may use trusted service providers to operate our communications, booking, and payment services. We take reasonable measures to protect the information entrusted to us.'
    }, {
      heading: 'Your choices',
      body: 'You may contact us to request access to, correction of, or deletion of personal information we hold, subject to applicable legal requirements.'
    }]
  },
  terms: {
    title: 'Terms of Service',
    sections: [{
      heading: 'Appointments',
      body: 'Appointments are subject to availability and are not confirmed until accepted by Ginger Briones Studio. Please arrive on time so we can provide the best possible experience.'
    }, {
      heading: 'Cancellations and changes',
      body: 'Please contact us as early as possible if you need to cancel or reschedule. Cancellation, late-arrival, deposit, and no-show policies may apply and will be communicated before booking.'
    }, {
      heading: 'Services and results',
      body: 'Recommendations and results are personalized to each guest. Hair history, condition, maintenance, and aftercare can affect outcomes. We may decline or modify a requested service when necessary for hair health or safety.'
    }, {
      heading: 'Contact',
      body: 'For questions about these terms or our services, please contact Ginger Briones Studio at (347) 888-6047.'
    }]
  }
} as const;
export function LegalModal({
  legalDocument,
  onClose
}: LegalModalProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!legalDocument) {
      return undefined;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.document.addEventListener('keydown', handleKeyDown);
    closeButtonRef.current?.focus();
    return () => window.document.removeEventListener('keydown', handleKeyDown);
  }, [legalDocument, onClose]);
  if (!legalDocument) {
    return null;
  }
  const content = legalContent[legalDocument];
  const titleId = `${legalDocument}-policy-title`;
  return <div className="fixed inset-0 z-[110] flex items-end justify-center bg-dark/55 px-4 py-4 sm:items-center sm:p-6" role="presentation" onMouseDown={event => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  }} data-id="element-248" data-mp="1b8uaz65wqoqm">
      <section role="dialog" aria-modal="true" aria-labelledby={titleId} className="max-h-[88svh] w-full max-w-2xl overflow-y-auto border border-gold/35 bg-warm-white shadow-[0_24px_70px_rgba(44,33,24,0.34)]" data-id="element-249" data-mp="1686lioxbufyi">
        <div className="sticky top-0 flex items-start justify-between gap-5 border-b border-brand-border bg-warm-white px-6 py-5 sm:px-8" data-id="element-250" data-mp="1l572bxdzcqi7">
          <div data-id="element-251" data-mp="1p57sgfafyq53">
            <p className="font-body text-[10px] font-semibold uppercase tracking-[3px] text-gold" data-id="element-252" data-mp="1p4po0vdml5m3">
              Ginger Briones Studio
            </p>
            <h2 id={titleId} className="mt-1 font-serif text-3xl text-dark sm:text-4xl" data-id="element-253" data-mp="2hnd4fxhofwgu">
              {content.title}
            </h2>
            <p className="mt-1 font-body text-xs text-soft" data-id="element-254" data-mp="u03541h7sqk7">Effective July 2026</p>
          </div>
          <button ref={closeButtonRef} type="button" onClick={onClose} aria-label="Close legal notice" className="flex h-10 w-10 shrink-0 items-center justify-center border border-brand-border text-mid transition-colors hover:border-gold hover:text-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2" data-id="element-255" data-mp="oynfrx807xw0">
            <XIcon className="h-5 w-5" strokeWidth={1.5} data-id="element-256" data-mp="17rzqlmodo27m" />
          </button>
        </div>

        <div className="space-y-7 px-6 py-7 sm:px-8 sm:py-9" data-id="element-257" data-mp="12x3otutca0hb">
          <p className="font-body text-sm font-light leading-7 text-mid" data-id="element-258" data-mp="ywju0xw8n8yc">
            This notice describes the policies of Ginger Briones Studio, located at 96-02 37th Ave, Corona, NY 11368.
          </p>
          {content.sections.map(section => <section key={section.heading} data-id="element-259" data-mp="39biplc3nlzn4">
              <h3 className="font-serif text-2xl text-dark" data-id="element-260" data-mp="39ckd4g1uwelq">{section.heading}</h3>
              <p className="mt-2 font-body text-sm font-light leading-7 text-mid" data-id="element-261" data-mp="3ea3tpmm113gu">{section.body}</p>
            </section>)}
        </div>
      </section>
    </div>;
}